import { useContext, useMemo, useCallback } from 'react';
import { SystemSettingsContext } from '../contexts/SystemSettingsContext';
import { 
  getCurrentBusinessDate as getCurrentBusinessDateUtil,
  getAdjustedDate as getAdjustedDateUtil,
} from '../utils/dateUtils';
import { format } from 'date-fns';

/**
 * 営業日を考慮した日付操作のためのカスタムフック
 * SystemSettingsContextから営業時間を取得し、dateUtilsに渡す
 */
export const useBusinessDate = () => {
  const { businessHoursSimple, loading } = useContext(SystemSettingsContext);

  const businessDate = useMemo(() => {
    // 営業時間情報が利用可能になるまで計算を遅延させる
    if (!businessHoursSimple) {
      return null;
    }
    return getCurrentBusinessDateUtil(businessHoursSimple);
  }, [businessHoursSimple]);
  
  const getAdjustedDate = useCallback((date: Date): Date => {
    // 営業時間がない場合は、調整せずに元の日付を返す
    if (!businessHoursSimple) {
      return date;
    }
    return getAdjustedDateUtil(businessHoursSimple, date);
  }, [businessHoursSimple]);

  const formatBusinessDate = useCallback((date: Date, formatStr: string = 'yyyy-MM-dd'): string => {
    if (!businessHoursSimple) {
      // businessHoursSimpleがまだ読み込まれていない場合はデフォルトのフォーマットを返す
      return format(date, formatStr);
    }
    const adjustedDate = getAdjustedDateUtil(businessHoursSimple, date);
    return format(adjustedDate, formatStr);
  }, [businessHoursSimple]);

  const value = useMemo(() => ({
    businessDate,
    getAdjustedDate,
    formatBusinessDate,
    isLoading: loading,
    businessHours: businessHoursSimple,
  }), [businessDate, getAdjustedDate, formatBusinessDate, loading, businessHoursSimple]);

  return value;
};

export default useBusinessDate; 